import { Component, OnInit } from '@angular/core';
import { FacilityItemInformation } from 'src/app/common/models/facilityItemInformation';
import { MatDialog } from '@angular/material/dialog';
import { ItemModalComponent } from 'src/app/_layouts/components/modal/import-item/import-item.component';
import { EditItemComponent } from 'src/app/_layouts/components/modal/edit-item/edit-item.component';
import { ItemAvailabilityComponent } from '../../components/modal/item-availability/item-availability.component';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { ItemsService } from 'src/app/services/items.service';
import { ToastrService } from 'ngx-toastr';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { ItemGroup } from 'src/app/common/models/itemGroup';
import { ItemGroupHelpers } from 'src/app/common/itemGroupHelpers';
import { UnimportedItemGroup } from 'src/app/common/models/unimportedItemGroup';
import { UnimportedItemGroupService } from 'src/app/services/unimported-item-group.service';
import { LoggingService } from 'src/app/services/logging.service';
import { ItemGroupEntity } from 'src/app/common/models/itemGroupEntity';

import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
  MatExpansionPanelContent,
} from '@angular/material/expansion';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { UnimportedItemGroupTileComponent } from '../../components/unimported-item-group-tile/unimported-item-group-tile.component';
import { ManageItemFilterComponent } from '../../components/manage-item-filter/manage-item-filter.component';
import { ItemGroupTileComponent } from '../../components/item-group-tile/item-group-tile.component';
import { ResolveInconsistencyComponent } from '../../components/modal/resolve-inconsistency/resolve-inconsistency.component';

@Component({
  selector: 'app-manage-items',
  templateUrl: './manage-items.component.html',
  styleUrls: ['./manage-items.component.scss'],
  standalone: true,
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatExpansionPanelContent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    UnimportedItemGroupTileComponent,
    ManageItemFilterComponent,
    ItemGroupTileComponent,
  ],
})
export class ManageItemsComponent implements OnInit {
  public importItemPanelOpenState = false;
  public updatingData = false;
  public numberOfFilteredItems: number | null = null;
  public departments: string[] = [];
  public authManager: AuthManager;
  public itemGroupsToImport: UnimportedItemGroup[] = [];
  public menuItems: FacilityItemInformation[] = [];
  public filteredItemGroupEntities: ItemGroupEntity[] = [];
  private setup = false;
  private loadingPoll = 0;
  private currentFacilityId: string | any;

  constructor(
    private _facilityManager: FacilityManager,
    private ItemService: ItemsService,
    private _unimportedItemGroupService: UnimportedItemGroupService,
    private logger: LoggingService,
    private auth: AuthManager,
    private toastr: ToastrService,
    public dialog: MatDialog,
  ) {
    this.currentFacilityId = this._facilityManager.getCurrentFacility();
    this.updatingData = true;
    this.authManager = this.auth;
  }

  public ngOnInit(): void {
    this.auth.isLoggedIn$.subscribe((isAuthed) => {
      if (isAuthed) {
        this._facilityManager.facilityIdUpdates$.subscribe((fId) => {
          if (this.currentFacilityId !== fId || !this.setup) {
            this.setup = true;
            this.currentFacilityId = fId;
            this.refreshItemVariables(fId);
          }
        });
      }
    });
  }

  private refreshItemVariables(facilityId: string): void {
    this.updatingData = true;
    this.ItemService.getItems(facilityId).subscribe((d) => {
      this.menuItems = d;
      const commodityItemGroups = ItemGroupHelpers.getCommodityItemGroups(d);
      const nonCommodityGroups = ItemGroupHelpers.getNonCommodityItemGroups(d);

      const sortingGroups = Array.prototype
        .concat(commodityItemGroups, nonCommodityGroups)
        .sort((a, b) => parseInt(a.itemNumber) - parseInt(b.itemNumber));
      this.filteredItemGroupEntities = sortingGroups;
      this.loadingPollCalculation();
    });

    this.ItemService.getUnimportedItems(facilityId).subscribe((d) => {
      this.itemGroupsToImport = this._unimportedItemGroupService.mapUnimportedItemsToItemGroupArray(d);
      this.loadingPollCalculation();
    });
  }

  private loadingPollCalculation(): void {
    this.loadingPoll++;
    if (this.loadingPoll === 2) {
      setTimeout(() => {
        this.updatingData = false;
      }, 500);
      this.loadingPoll = 0;
    }
  }

  public updateFilteredItems(items: FacilityItemInformation[]): void {
    let relatedCommodityItems: FacilityItemInformation[] = [];
    const commodityItemIds: number[] = [];
    const independentItems: FacilityItemInformation[] = [];
    const externalItemGroupItems: FacilityItemInformation[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.commodityGroupId === undefined || item.commodityGroupId! <= 0) {
        if (item.externalItemNumber === null || item.externalItemNumber === undefined) {
          independentItems.push(item);
        } else {
          externalItemGroupItems.push(item);
        }
      } else {
        commodityItemIds.push(item.commodityGroupId!);
      }
    }

    // need to get related commodity items to items in above array
    relatedCommodityItems = this.menuItems.filter((mi) => commodityItemIds.includes(mi.commodityGroupId || 0));
    const relatedExternalItems = this.getRelatedItemsByExternalItemNumber(externalItemGroupItems);
    let itemsToTransform = Array.prototype.concat(relatedCommodityItems, relatedExternalItems);
    itemsToTransform = Array.prototype.concat(itemsToTransform, independentItems);
    this.filteredItemGroupEntities = [...this.createEntityGroups(itemsToTransform)];
    this.numberOfFilteredItems = this.filteredItemGroupEntities.length;
  }

  public openItemImportDialog(unimportedItemGroup: UnimportedItemGroup): void {
    const dialogRef = this.dialog.open(ItemModalComponent, {
      width: '1000px',
      maxHeight: '1000px',
      maxWidth: '1200px',
      data: unimportedItemGroup,
    });

    dialogRef.afterClosed().subscribe({
      next: (result: UnimportedItemGroup) => {
        if (result) {
          const index = this.findUnimportedItemIndexByItemNumber(this.itemGroupsToImport, result.itemNumber);
          if (index > -1) {
            const _ = this.itemGroupsToImport.splice(index, 1);
            this.itemGroupsToImport = [...this.itemGroupsToImport];
            result.unimportedItems.forEach((item) => {
              this.ItemService.getItem(this.currentFacilityId, item.commerceItemNumber)
                .subscribe({
                  next: (itemInfo) => {
                    this.menuItems.push(itemInfo);
                  },
                  error: (err) => {
                    this.toastr.error('Error while importing item: ' + err);
                  },
                })
                .add(() => {
                  this.updateFilteredItems(this.menuItems);
                });
            });
          }
        }
      },
    });
  }

  public openItemEditDialog(itemGroupEntity: ItemGroupEntity): void {
    const dialogRef = this.dialog.open(EditItemComponent, {
      maxHeight: '1000px',
      maxWidth: '1200px',
      width: '700px',
      data: {
        itemGroupEntity: structuredClone(itemGroupEntity),
        menuItems: structuredClone(this.menuItems),
      },
    });
    dialogRef.afterClosed().subscribe((itemGroupEntities: ItemGroupEntity[]) => {
      if (itemGroupEntities !== undefined && itemGroupEntities.length > 0) {
        const updatedItems: FacilityItemInformation[] = [];
        itemGroupEntities.forEach((itemGroupEntity) => {
          itemGroupEntity.itemGroups.forEach((itemGroup) => {
            updatedItems.push(...itemGroup.items);
          });
        });
        this._replaceMenuItems(updatedItems);
      }
    });
  }

  public openAvailabilityDialog(itemGroup: ItemGroup): void {
    itemGroup.items.forEach((item) => {
      item.isAvailable = !item.isAvailable;
    });
    itemGroup.isAvailable = !itemGroup.isAvailable;

    const dialogRef = this.dialog.open(ItemAvailabilityComponent, {
      maxHeight: '1000px',
      maxWidth: '1200px',
      width: '1000px',
      data: itemGroup,
    });

    dialogRef.afterClosed().subscribe((resultItemGroup: ItemGroup | undefined) => {
      if (!resultItemGroup) {
        itemGroup.isAvailable = !itemGroup.isAvailable;
        itemGroup.items.forEach((item) => {
          item.isAvailable = !item.isAvailable;
        });
      } else {
        resultItemGroup.items.forEach((item) => {
          item.isAvailable = resultItemGroup.isAvailable;
        });
        this._replaceMenuItems(resultItemGroup.items);
      }
    });
  }

  private _replaceMenuItems(updatedItems: FacilityItemInformation[]): void {
    for (const item of updatedItems) {
      const index = this.menuItems.findIndex((menuItem) => menuItem.commerceItemNumber === item.commerceItemNumber);
      if (index >= 0) {
        this.menuItems[index] = item;
      }
    }

    this.menuItems = [...this.menuItems];

    const visibleCommerceItemNumbers = new Set(
      this.filteredItemGroupEntities.flatMap((entity) =>
        entity.itemGroups.flatMap((group) => group.items.map((i) => i.commerceItemNumber)),
      ),
    );
    const visibleItems = this.menuItems.filter((i) => visibleCommerceItemNumbers.has(i.commerceItemNumber));
    this.updateFilteredItems(visibleItems);
  }

  public openResolveInconsistencyDialog(itemGroupEntity: ItemGroupEntity): void {
    const dialogRef = this.dialog.open(ResolveInconsistencyComponent, {
      width: '700px',
      maxHeight: '1000px',
      maxWidth: '1200px',
      data: structuredClone(itemGroupEntity),
    });
    dialogRef.afterClosed().subscribe((updatedItems: FacilityItemInformation[] | undefined) => {
      if (updatedItems && updatedItems.length > 0) {
        this._replaceMenuItems(updatedItems);
      }
    });
  }

  public externalItemNumberStockRefresh(itemGroupEntity: ItemGroupEntity): void {
    // Call patchOrderableItemPrepLocation with empty operations for each item to trigger a refresh
    itemGroupEntity.itemGroups.forEach((itemGroup) => {
      itemGroup.items.forEach((item) => {
        this.ItemService.patchOrderableItemPrepLocation(
          this.currentFacilityId,
          item.commerceItemNumber,
          [], // Empty operations array to make no changes while still triggering a refresh
        ).subscribe({
          next: (r) => {
            this.toastr.success(`Successfully refreshed item ${item.commerceItemNumber}`);
          },
          error: (e) => {
            this.toastr.error(`Error while refreshing item ${item.commerceItemNumber}: ` + e);
            this.logger.logException(e);
            this.logger.logTrace('Error while refreshing item during stock refresh', {
              commerceItemNumber: item.commerceItemNumber,
              facilityId: this.currentFacilityId,
            });
          },
        });
      });
    });

    // Get all commerce item numbers for logging
    const allCommerceItemNumbers: string[] = [];
    itemGroupEntity.itemGroups.forEach((itemGroup) => {
      itemGroup.items.forEach((item) => {
        allCommerceItemNumbers.push(item.commerceItemNumber);
      });
    });

    this.logger.logEvent('Refreshed Items: ' + itemGroupEntity.itemNumber, {
      items: allCommerceItemNumbers,
      selectedFacility: this.currentFacilityId,
      currentFacility: this.currentFacilityId,
    });
  }

  private getRelatedItemsByExternalItemNumber(items: FacilityItemInformation[]): FacilityItemInformation[] {
    const relatedItems: FacilityItemInformation[] = [];
    const relatedItemMap = new Map<string, string>();
    items.forEach((item) => {
      if (!relatedItemMap.has(item.externalItemNumber)) {
        const relatedExternalItemNumbers = this.menuItems.filter((mi) => {
          if (mi.externalItemNumber === null || item.externalItemNumber === null) {
            return false;
          }
          return mi.externalItemNumber === item.externalItemNumber;
        });
        relatedItems.push.apply(relatedItems, relatedExternalItemNumbers);
        relatedItemMap.set(item.externalItemNumber, item.externalItemNumber);
      }
    });
    return relatedItems;
  }

  private findUnimportedItemIndexByItemNumber(unimportedItemGroup: UnimportedItemGroup[], itemNumber: string): number {
    return unimportedItemGroup.findIndex((g) => {
      return g.itemNumber === itemNumber;
    });
  }

  public filteredItemGroupEntityTrackBy(index: any, itemGroupEntity: ItemGroupEntity): string {
    return itemGroupEntity.itemNumber;
  }

  public importItemGroupTrackBy(index: any, itemToImport: UnimportedItemGroup): string {
    return itemToImport.itemNumber;
  }

  private createEntityGroups(items: FacilityItemInformation[]): ItemGroupEntity[] {
    const commodityItemGroups = ItemGroupHelpers.getCommodityItemGroups(items);
    const nonCommodityGroups = ItemGroupHelpers.getNonCommodityItemGroups(items);
    return Array.prototype
      .concat(commodityItemGroups, nonCommodityGroups)
      .sort((a, b) => parseInt(a.itemNumber) - parseInt(b.itemNumber));
  }
}
